/**
 * Style finder — two-question guide (audience → category) that routes to a collection.
 * Markup and destination config come from snippets/style-finder.liquid.
 */
if (!customElements.get('style-finder')) {
  customElements.define(
    'style-finder',
    class StyleFinder extends HTMLElement {
      constructor() {
        super();
        this.storageKey = 'vearo_style_finder';
        this.seenKey = 'vearo_style_finder_seen';
        this.closeDuration = 600;
        this.flightDuration = 1100;
        this.flightBackDuration = 750;
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      }

      connectedCallback() {
        const configEl = this.querySelector('[data-style-finder-config]');
        if (!configEl) return;
        try {
          this.config = JSON.parse(configEl.textContent);
        } catch (e) {
          return;
        }
        this.decodeText(this.config);

        this.launcher = this.querySelector('[data-style-finder-launcher]');
        this.panel = this.querySelector('.style-finder__panel');
        this.backdrop = this.querySelector('.style-finder__backdrop');
        this.progress = this.querySelector('[data-style-finder-progress]');
        this.question = this.querySelector('[data-style-finder-question]');
        this.options = this.querySelector('[data-style-finder-options]');
        this.backButton = this.querySelector('[data-style-finder-back]');
        this.restartButton = this.querySelector('[data-style-finder-restart]');
        this.continueButton = this.querySelector('[data-style-finder-continue]');
        this.status = this.querySelector('[data-style-finder-status]');

        this.state = Object.assign({ step: 'audience', audience: null, category: null }, this.readState());
        this.state.step = 'audience';

        this.launcher.addEventListener('click', () => this.toggle());
        this.querySelectorAll('[data-style-finder-close]').forEach((el) => {
          el.addEventListener('click', () => this.close());
        });
        this.backButton.addEventListener('click', () => this.back());
        this.restartButton.addEventListener('click', () => this.restart());
        this.continueButton.addEventListener('click', () => this.goTo('category'));
        this.addEventListener('keydown', (event) => this.onKeyDown(event));
        this.onOutsideClick = (event) => {
          // composedPath, not contains(): options are re-rendered (detached) while the click bubbles.
          if (this.isOpen() && !event.composedPath().includes(this)) this.close(false);
        };

        this.scheduleAutoOpen();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onOutsideClick);
        clearTimeout(this.autoOpenTimer);
        this.cancelClose();
      }

      /* ---------- state ---------- */

      readState() {
        try {
          return JSON.parse(sessionStorage.getItem(this.storageKey)) || {};
        } catch (e) {
          return {};
        }
      }

      saveState() {
        try {
          sessionStorage.setItem(
            this.storageKey,
            JSON.stringify({ audience: this.state.audience, category: this.state.category })
          );
        } catch (e) {
          /* storage unavailable — the flow still works without it */
        }
      }

      getAudience(key = this.state.audience) {
        return this.config.audiences.find((audience) => audience.key === key);
      }

      getCategory(audience, key) {
        return audience ? audience.categories.find((category) => category.key === key) : undefined;
      }

      // Translated strings arrive HTML-escaped (e.g. &#39;); turn them back into plain text.
      decodeText(node) {
        const decoder = document.createElement('textarea');
        const walk = (value) => {
          Object.keys(value).forEach((key) => {
            if (key === 'url') return;
            if (typeof value[key] === 'string') {
              decoder.innerHTML = value[key];
              value[key] = decoder.value;
            } else if (value[key] && typeof value[key] === 'object') {
              walk(value[key]);
            }
          });
        };
        walk(node);
      }

      // A category without its own collection opens the closest one instead:
      // the same category for everyone, then the audience's "all" collection, then everything.
      getDestination(audience, category) {
        if (category.url) return category.url;
        const unisex = this.getAudience('unisex');
        const shared = audience !== unisex ? this.getCategory(unisex, category.key) : undefined;
        if (shared && shared.url) return shared.url;
        const audienceAll = this.getCategory(audience, 'all');
        if (audienceAll && audienceAll.url) return audienceAll.url;
        return this.config.shopAll.url;
      }

      /* ---------- open / close ---------- */

      isOpen() {
        return this.classList.contains('is-open');
      }

      toggle() {
        if (this.isOpen()) {
          this.close();
        } else {
          this.open();
        }
      }

      // First page of a visit: glide the panel in by itself, once per browser session.
      scheduleAutoOpen() {
        if (this.dataset.autoOpen !== 'true') return;
        if (window.Shopify && window.Shopify.designMode) return;
        if (this.readFlag(this.seenKey)) return;

        // Let the intro splash (snippets/vearo-splash.liquid) finish first.
        if (document.documentElement.classList.contains('vearo-splash-active')) {
          document.addEventListener('vearo:splash:done', () => this.scheduleAutoOpen(), { once: true });
          return;
        }

        const delay = Number(this.dataset.autoOpenDelay) || 0;
        this.autoOpenTimer = setTimeout(() => {
          const pageIsBusy = /(^|\s)overflow-hidden/.test(document.body.className);
          if (this.isOpen() || pageIsBusy || this.readFlag(this.seenKey)) return;
          this.open({ auto: true });
        }, delay);
      }

      readFlag(key) {
        try {
          return sessionStorage.getItem(key) === '1';
        } catch (e) {
          return false;
        }
      }

      open({ auto = false } = {}) {
        clearTimeout(this.autoOpenTimer);
        this.cancelClose();
        try {
          sessionStorage.setItem(this.seenKey, '1');
        } catch (e) {
          /* storage unavailable */
        }

        // The panel always opens at the bottom, anchored to the corner badge — on arrival too.
        // (The centre-screen modal variant, .is-centered + fly(), is currently unused.)
        this.classList.remove('is-centered');
        this.panel.setAttribute('aria-modal', 'false');
        this.panel.hidden = false;
        this.backdrop.hidden = false;
        this.launcher.setAttribute('aria-expanded', 'true');
        this.render();
        this.panel.getBoundingClientRect(); // flush styles so the open transition runs
        this.classList.add('is-open');
        // An automatic open must not pull keyboard focus away from the page.
        if (!auto) this.panel.focus({ preventScroll: true });
        document.addEventListener('click', this.onOutsideClick);
      }

      close(restoreFocus = true) {
        this.classList.remove('is-open');
        this.launcher.setAttribute('aria-expanded', 'false');
        document.removeEventListener('click', this.onOutsideClick);
        if (restoreFocus && this.contains(document.activeElement)) this.launcher.focus({ preventScroll: true });

        // Keep the panel rendered until it has finished closing.
        this.cancelClose();
        const hide = () => {
          this.panel.hidden = true;
          this.backdrop.hidden = true;
          this.classList.remove('is-centered');
          if (this.flight) this.flight.cancel();
          this.flight = null;
        };
        const flight = this.classList.contains('is-centered') ? this.fly(true) : null;
        // A timer, not flight.finished: animations do not tick in a background tab.
        this.closeTimer = setTimeout(hide, flight ? this.flightBackDuration + 50 : this.closeDuration);
      }

      cancelClose() {
        clearTimeout(this.closeTimer);
        if (this.flight) this.flight.cancel();
        this.flight = null;
      }

      /**
       * Centred panel only: one continuous flight between the corner badge and the centre of the
       * screen. It sweeps along a curve, growing, un-tilting and sharpening as it goes, then settles
       * with a slight overshoot. Closing plays the same flight backwards, into the badge.
       */
      fly(reverse) {
        if (!this.panel.animate || this.reducedMotion.matches) return null;

        const badge = this.launcher.getBoundingClientRect();
        const fromX = badge.left + badge.width / 2 - window.innerWidth / 2;
        const fromY = badge.top + badge.height / 2 - window.innerHeight / 2;
        const steps = 48;
        const frames = [];

        for (let i = 0; i <= steps; i += 1) {
          const t = i / steps;
          const progress = 1 - Math.pow(1 - Math.pow(t, 1.3), 3.4); // soft start, long glide to rest
          const left = 1 - progress;
          // Quadratic curve from the badge to the centre: stays low at first, then lifts.
          const x = left * left * fromX + 2 * left * progress * fromX * 0.25;
          const y = left * left * fromY + 2 * left * progress * fromY * 0.95;
          const settle = t > 0.55 ? Math.sin((Math.PI * (t - 0.55)) / 0.45) * 0.02 : 0;
          const scale = 0.05 + 0.95 * Math.pow(progress, 1.2) + settle;

          frames.push({
            offset: t,
            opacity: Math.min(1, t * 6),
            filter: `blur(${(left * 6).toFixed(2)}px)`,
            transform:
              `translate(calc(-50% + ${x.toFixed(1)}px), calc(-50% + ${y.toFixed(1)}px)) ` +
              `rotate(${(left * -7).toFixed(2)}deg) scale(${scale.toFixed(4)})`,
          });
        }

        if (this.flight) this.flight.cancel();
        this.flight = this.panel.animate(frames, {
          duration: reverse ? this.flightBackDuration : this.flightDuration,
          direction: reverse ? 'reverse' : 'normal',
          easing: 'linear',
          fill: 'both',
        });
        if (!reverse) {
          // Hand back to the stylesheet once the panel has landed.
          const flight = this.flight;
          flight.finished.then(() => {
            if (this.flight === flight) {
              flight.cancel();
              this.flight = null;
            }
          }, () => {});
        }
        return this.flight;
      }

      onKeyDown(event) {
        if (!this.isOpen()) return;

        if (event.key === 'Escape') {
          event.stopPropagation();
          this.close();
          return;
        }

        // The mobile bottom sheet and the centred arrival panel sit over a backdrop, so keep Tab inside.
        if (event.key === 'Tab' && (this.mobileQuery.matches || this.classList.contains('is-centered'))) {
          const focusable = Array.from(this.panel.querySelectorAll('button, a[href]')).filter(
            (el) => !el.hidden && el.offsetParent !== null
          );
          if (!focusable.length) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      }

      /* ---------- navigation between steps ---------- */

      goTo(step) {
        this.state.step = step;
        this.render();
        this.question.focus({ preventScroll: true });
      }

      back() {
        this.goTo('audience');
      }

      restart() {
        this.state.audience = null;
        this.state.category = null;
        this.saveState();
        this.goTo('audience');
      }

      selectAudience(key) {
        if (this.state.audience !== key) this.state.category = null;
        this.state.audience = key;
        this.saveState();
        this.goTo('category');
      }

      selectCategory(category) {
        // Categories are real links, so the browser handles the navigation.
        this.state.category = category.key;
        this.saveState();
        this.status.textContent = this.config.strings.redirecting.replace('[category]', category.label);
      }

      /* ---------- rendering ---------- */

      render() {
        const { strings } = this.config;
        const audience = this.getAudience();
        if (!audience && this.state.step !== 'audience') this.state.step = 'audience';
        const step = this.state.step;

        this.options.textContent = '';
        this.progress.textContent = strings.step
          .replace('[current]', step === 'audience' ? '1' : '2')
          .replace('[total]', '2');

        if (step === 'audience') {
          this.question.textContent = strings.questionAudience;
          this.config.audiences.forEach((item) => {
            const button = this.createOption('button', item.label, item.key === this.state.audience);
            button.setAttribute('aria-pressed', String(item.key === this.state.audience));
            button.addEventListener('click', () => this.selectAudience(item.key));
          });
        } else {
          this.question.textContent = strings.questionCategory;
          audience.categories.forEach((category) => {
            const selected = category.key === this.state.category;
            const option = this.createOption('a', category.label, selected);
            option.href = this.getDestination(audience, category);
            if (selected) option.setAttribute('aria-current', 'true');
            option.addEventListener('click', () => this.selectCategory(category));
          });
        }

        this.backButton.hidden = step === 'audience';
        this.restartButton.hidden = step === 'audience';
        this.continueButton.hidden = !(step === 'audience' && audience);
      }

      createOption(tagName, label, selected) {
        const item = document.createElement('li');
        const option = document.createElement(tagName);
        if (tagName === 'button') option.type = 'button';
        option.className = 'style-finder__option';
        option.classList.toggle('is-selected', selected);
        option.textContent = label;
        item.appendChild(option);
        this.options.appendChild(item);
        return option;
      }
    }
  );
}
